// app/src/AddRoomModal.jsx
// Extracted from App.jsx so the onboarding wizard (Onboarding.jsx) can reuse
// the same room-creation modal that the Rooms tab uses.

import { useState } from 'react';
import { BASELINE_TYPES_EN, slugifyType, resolveTypeLabel, getKnownTypeKeys } from './room-types.js';

// ── Brand atoms (mirrors CL / FONT in App.jsx and Onboarding.jsx) ──────────
const CL = {
  sand:  '#F5EFE6',
  cream: '#FFFDF8',
  bark:  '#3D2B1F',
  gold:  '#C9A96E',
  goldL: '#E8D5B0',
  olive: '#5A7247',
  terra: '#C0562B',
  stone: '#8B7D6B',
};
const FONT = {
  display: "'Cormorant Garamond', Georgia, serif",
  body:    "'Figtree', 'Helvetica Neue', -apple-system, sans-serif",
};

// ── Inline form for creating a per-property custom room type ────────────────
// Shared by AddRoomModal (below) and the RoomRow inline edit panel in App.jsx.
export function CustomTypeForm({ property, lang, onCancel, onSelected, onUpdateRoomTypeInfo }) {
  const [labelEn, setLabelEn] = useState('');
  const [labelBg, setLabelBg] = useState('');
  const [err, setErr] = useState('');

  const hasSecondary = (property?.lang || '').includes(',');
  const primaryLang = (property?.lang || 'en').split(',')[0];
  const customTypeKeys = getKnownTypeKeys(property).filter(k => !BASELINE_TYPES_EN[k]);

  async function addType() {
    const primaryLabel = primaryLang === 'bg' ? labelBg : labelEn;
    if (!primaryLabel.trim()) {
      setErr(lang === 'bg' ? 'Етикетът е задължителен' : 'Label is required');
      return;
    }
    const newKey = slugifyType(primaryLabel, customTypeKeys);

    // Slug merged into a baseline → just select it, no DB write.
    if (BASELINE_TYPES_EN[newKey]) { onSelected(newKey); return; }
    // Slug collides with an existing custom type → silent reuse.
    if (customTypeKeys.includes(newKey)) { onSelected(newKey); return; }

    // Brand-new custom type: persist labels, then select.
    // Only advance if persistence succeeded — otherwise we'd save a room with
    // a type whose labels never made it to the DB.
    const labels = {};
    if (labelEn.trim()) labels.en = labelEn.trim();
    if (labelBg.trim()) labels.bg = labelBg.trim();
    const nextInfo = {
      ...(property.room_type_info || {}),
      [newKey]: { ...((property.room_type_info || {})[newKey] || {}), labels },
    };
    const ok = await onUpdateRoomTypeInfo(nextInfo);
    if (!ok) return;
    onSelected(newKey);
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: CL.stone, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1, display: 'block' };
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 16, boxSizing: 'border-box', background: '#fff', color: CL.bark, fontFamily: 'inherit' };

  return (
    <div style={{ marginTop: 8, padding: 12, background: CL.sand, borderRadius: 8 }}>
      <label style={labelStyle}>
        {hasSecondary
          ? (primaryLang === 'bg' ? 'Етикет (Български)' : 'Label (English)')
          : (lang === 'bg' ? 'Етикет' : 'Type label')}
      </label>
      <input
        value={primaryLang === 'bg' ? labelBg : labelEn}
        onChange={e => {
          if (primaryLang === 'bg') setLabelBg(e.target.value); else setLabelEn(e.target.value);
          setErr('');
        }}
        placeholder={lang === 'bg' ? 'напр. Стая с морска гледка' : 'e.g. Sea view room'}
        style={inputStyle}
      />
      {hasSecondary && (
        <div style={{ marginTop: 8 }}>
          <label style={labelStyle}>
            {primaryLang === 'bg' ? 'Label (English)' : 'Етикет (Български)'}
          </label>
          <input
            value={primaryLang === 'bg' ? labelEn : labelBg}
            onChange={e => {
              if (primaryLang === 'bg') setLabelEn(e.target.value); else setLabelBg(e.target.value);
            }}
            placeholder={primaryLang === 'bg' ? 'e.g. Sea view room' : 'напр. Стая с морска гледка'}
            style={inputStyle}
          />
        </div>
      )}
      {err && <div style={{ fontSize: 11, color: CL.terra, marginTop: 4 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button onClick={onCancel}
          style={{ padding: '6px 12px', background: 'none', color: CL.stone, border: 'none', fontSize: 12, cursor: 'pointer' }}>
          {lang === 'bg' ? 'Отказ' : 'Cancel'}
        </button>
        <button onClick={addType}
          style={{ padding: '6px 14px', background: CL.bark, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {lang === 'bg' ? 'Добави тип' : 'Add type'}
        </button>
      </div>
    </div>
  );
}

// ── AddRoomModal ────────────────────────────────────────────────────────────
// Props:
//   property            — the current property object (for currency, lang, room_type_info)
//   lang                — 'en' | 'bg'
//   onCancel()          — close without saving
//   onSubmit(draft)     — called with { name, type, capacity, maxAdults, maxChildren, basePrice }
//   onUpdateRoomTypeInfo(nextInfo) — async; persists custom type labels; return true/false
export function AddRoomModal({ property, lang, onCancel, onSubmit, onUpdateRoomTypeInfo }) {
  const cur = property?.currency === 'GBP' ? '£' : '€';
  const knownTypes = getKnownTypeKeys(property);

  const [name, setName] = useState('');
  const [type, setType] = useState('room');
  const [capacity, setCapacity] = useState(4);
  const [maxAdults, setMaxAdults] = useState('');
  const [maxChildren, setMaxChildren] = useState('');
  const [basePrice, setBasePrice] = useState(60);
  const [showAdv, setShowAdv] = useState(false);
  const [nameErr, setNameErr] = useState(false);
  const [capacityErr, setCapacityErr] = useState('');
  const [customOpen, setCustomOpen] = useState(false);

  const L = lang === 'bg' ? {
    title: 'Нова стая',
    name: 'Име', namePh: '1',
    nameHelp: 'Повечето хотели използват номера (1, 2, 12). Кратко, показва се на гостите.',
    type: 'Тип', sleeps: 'Капацитет',
    maxAdults: 'Макс. възрастни', maxChildren: 'Макс. деца', noLimit: 'без лимит',
    price: 'Цена на нощ', priceHelp: 'Една стартова цена. Сезонни цени можете да добавите от тук по-късно.',
    adv: 'Разширени: лимит за възрастни / деца',
    cancel: 'Отказ', save: 'Добави стая',
    nameReq: 'Името е задължително',
  } : {
    title: 'New room',
    name: 'Name', namePh: '1',
    nameHelp: 'Most hotels use room numbers (1, 2, 12). Keep it short — it shows to guests.',
    type: 'Type', sleeps: 'Sleeps',
    maxAdults: 'Max adults', maxChildren: 'Max children', noLimit: 'no limit',
    price: 'Price per night', priceHelp: 'One starting price for now. Add seasonal pricing later from this tab.',
    adv: 'Advanced — limit by adults / children',
    cancel: 'Cancel', save: 'Add room',
    nameReq: 'Name is required',
  };

  function submit() {
    if (!name.trim()) { setNameErr(true); return; }
    const cap = Number(capacity) || 1;
    const adults = maxAdults === '' ? null : Number(maxAdults);
    const children = maxChildren === '' ? null : Number(maxChildren);
    if (adults !== null && cap < adults) {
      setCapacityErr(lang === 'bg' ? 'Капацитетът трябва да е ≥ макс. възрастни' : 'Sleeps must be ≥ max adults');
      return;
    }
    setCapacityErr('');
    onSubmit({ name: name.trim(), type, capacity: cap, maxAdults: adults, maxChildren: children, basePrice: Number(basePrice) || 0 });
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: CL.stone, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1, display: 'block' };
  const inputStyle = (err) => ({ width: '100%', padding: '10px 12px', border: '1px solid ' + (err ? CL.terra : CL.goldL), borderRadius: 8, fontSize: 16, boxSizing: 'border-box', background: '#fff', color: CL.bark, fontFamily: 'inherit' });

  return (
    <div data-testid="add-room-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: CL.cream, borderRadius: 12, padding: 20, maxWidth: 460, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontFamily: FONT.display, fontSize: 20, fontWeight: 500, color: CL.bark, marginBottom: 16 }}>{L.title}</div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>{L.name}</label>
          <input value={name} onChange={e => { setName(e.target.value); setNameErr(false); }} placeholder={L.namePh} style={inputStyle(nameErr)} />
          {nameErr && <div style={{ fontSize: 11, color: CL.terra, marginTop: 4 }}>{L.nameReq}</div>}
          <div style={{ fontSize: 11, color: CL.stone, marginTop: 4 }}>{L.nameHelp}</div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>{L.type}</label>
          <select value={type} onChange={e => {
            if (e.target.value === '__custom__') { setCustomOpen(true); return; }
            setType(e.target.value);
          }} style={inputStyle(false)}>
            {knownTypes.map(k => (
              <option key={k} value={k}>{resolveTypeLabel(k, property?.room_type_info, lang)}</option>
            ))}
            <option value="__custom__">
              {lang === 'bg' ? '+ Добави нов тип…' : '+ Add custom type…'}
            </option>
          </select>

          {customOpen && (
            <CustomTypeForm
              property={property}
              lang={lang}
              onCancel={() => setCustomOpen(false)}
              onSelected={(key) => { setType(key); setCustomOpen(false); }}
              onUpdateRoomTypeInfo={onUpdateRoomTypeInfo}
            />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>{L.sleeps}</label>
            <input type="number" min="1" max="20" value={capacity} onChange={e => { setCapacity(e.target.value); setCapacityErr(''); }} style={inputStyle(!!capacityErr)} />
            {capacityErr && <div style={{ fontSize: 11, color: CL.terra, marginTop: 4 }}>{capacityErr}</div>}
          </div>
          <div>
            <label style={labelStyle}>{L.price} ({cur})</label>
            <input type="number" min="0" value={basePrice} onChange={e => setBasePrice(e.target.value)} style={inputStyle(false)} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: CL.stone, marginTop: -6, marginBottom: 12 }}>{L.priceHelp}</div>

        <button onClick={() => setShowAdv(!showAdv)} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8,
          fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontFamily: FONT.body,
        }}>
          {showAdv ? '▾' : '▸'} {L.adv}
        </button>

        {showAdv && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{L.maxAdults}</label>
              <input type="number" min="1" max="20" value={maxAdults} placeholder={L.noLimit}
                onChange={e => setMaxAdults(e.target.value)} style={inputStyle(false)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{L.maxChildren}</label>
              <input type="number" min="0" max="20" value={maxChildren} placeholder={L.noLimit}
                onChange={e => setMaxChildren(e.target.value)} style={inputStyle(false)} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onCancel} style={{ padding: '10px 16px', background: 'transparent', color: CL.stone, border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>{L.cancel}</button>
          <button onClick={submit} style={{ padding: '10px 16px', background: CL.olive, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{L.save}</button>
        </div>
      </div>
    </div>
  );
}
